import fetch from 'axios';
import { fetchApi, postApi, getApi, deleteApi } from './utils';
import config from '@dragonfruit/config';
import { NOWTV_AUTH_TYPE } from '@dragonfruit/constants/authType';

const { DRAGON_FRUIT_ENDPOINT } = config;

const apiLogin = data =>
  fetchApi('api/v3/on_board/login', 'POST', {
    data,
  }).then(res => res);

const apiLogout = () => postApi('api/v2/logout').then(res => res);

const apiOauthLogin = data =>
  fetchApi('api/v2/login/oauth', 'POST', {
    data,
  });

const apiEmailSignup = data =>
  postApi('api/v3/on_board/email/register', { data }).then(res => res.data);

const apiPhoneSignup = data =>
  postApi('api/v4/on_board/phone/register', { data }).then(res => res.data);

const apiFacebookSignup = data =>
  postApi('api/v3/on_board/oauth_register', {
    data,
  }).then(res => res);

const apiGetOauthNowTVLoginUrl = () =>
  getApi('api/v4/oauth/authorize_url', {
    params: { provider: NOWTV_AUTH_TYPE },
  }).then(res => res.data);

const apiOauthNowTVLogin = code =>
  postApi('api/v4/on_board/authorize', {
    data: { provider: NOWTV_AUTH_TYPE, authorization_code: code },
  }).then(res => res.data);

// check unconfirmed_email
const apiVerifyEmail = email =>
  postApi('api/v3/on_board/email/verify_status', { data: { email } }).then(
    res => res.data
  );

// no check unconfirmed_email
const apiCheckEmail = email =>
  getApi('api/v2/users/check/email', { params: { email } }).then(
    res => res.data
  );

const apiVerifyPhoneSignup = ({ countryCode, phoneNumber, verificationCode }) =>
  postApi('api/v4/on_board/phone/verification', {
    data: {
      country_code: countryCode,
      phone: phoneNumber,
      verification_code: verificationCode,
    },
  }).then(res => res.data);

const apiCheckConfirmEmailToken = token =>
  postApi(`api/v3/on_board/email/register/${token}`).then(res => res.data);

const apiCheckUpdatedEmailToken = token =>
  fetchApi('api/v2/users/verify_email', 'POST', {
    data: {
      confirmation_token: token,
    },
  }).then(res => res.data);

const apiSendEmailVerification = ({ userId }) =>
  fetchApi(`api/v2/users/${userId}/resend_email_verification`, 'POST').then(
    res => res
  );

const apiResendPhoneSignupVerification = ({ countryCode, phoneNumber }) =>
  postApi('api/v4/on_board/phone/resend_verification_code', {
    data: {
      country_code: countryCode,
      phone: phoneNumber,
    },
  }).then(res => res.data);

const apiFetchRegions = (forTutorRegistration = false) =>
  getApi(`api/v2/regions?for_tutor_registration=${forTutorRegistration}`).then(
    res => res
  );

const apiFetchDistricts = regionId =>
  fetchApi(`api/v2/regions/${regionId}/districts`).then(res => res);

const apiFetchStudentDistricts = regionId =>
  getApi('api/v2/districts', {
    params: {
      region_id: regionId,
    },
  }).then(res => res);

const apiFetchSchools = ({ regionId, districtId, role }) =>
  getApi(`api/v2/regions/${regionId}/districts/${districtId}/schools`, {
    params: {
      role,
    },
  });

const apiFetchStudentSchools = districtId =>
  getApi(`api/v2/districts/${districtId}/schools`).then(res => res);

const apiFetchCurriculums = id =>
  getApi(`api/v2/regions/${id}/curriculums`).then(res => res);

const apiFetchUserInfo = ({ userId }) =>
  fetchApi(`api/v2/users/${userId}`, 'GET').then(res => res);

const apiFetchCurrentPlans = () =>
  fetchApi('api/v3/students/current_plans', 'GET').then(res => res.data);

const apiFetchBillingHistory = () =>
  fetchApi('api/v4/students/bill_histories', 'GET').then(res => res.data);

const apiFetchCancelSubscriptionMethod = subscriptionId =>
  fetchApi(
    `api/v3/students/subscriptions/${subscriptionId}/cancel_method`,
    'GET'
  ).then(res => res.data);

const apiPostCancelSubscription = (subscriptionId, data) =>
  fetchApi(
    `api/v3/students/subscriptions/${subscriptionId}/apply_for_cancellation`,
    'POST',
    {
      data,
    }
  ).then(res => res.data);

const apiResetUserPassword = ({
  userId,
  newPassword,
  currentPassword,
  confirmNewPassword,
}) =>
  fetchApi(`api/v2/users/${userId}/update_password`, 'PATCH', {
    data: {
      old_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: confirmNewPassword,
    },
  }).then(res => res);

const apiResetGuestPassword = ({ newPassword, token, confirmNewPassword }) =>
  fetchApi('api/v2/users/reset_password', 'PATCH', {
    data: {
      reset_password_token: token,
      new_password: newPassword,
      new_password_confirmation: confirmNewPassword,
    },
  }).then(res => res);

const apiResetUserEmail = ({ userId, email }) =>
  fetchApi(`api/v2/users/${userId}/update_email`, 'PATCH', {
    data: {
      new_email: email,
    },
  }).then(res => res);

const apiResetUserPhone = ({ userId, countryCode, phoneNumber }) =>
  fetchApi(`api/v2/users/${userId}/update_email`, 'PATCH', {
    data: {
      new_country_code: countryCode,
      new_phone_number: phoneNumber,
    },
  }).then(res => res);

const apiPauseSubscription = ({ subscriptionId, billingCycles }) =>
  fetchApi(`api/v3/braintree/subscriptions/${subscriptionId}/pause`, 'PATCH', {
    data: {
      billing_cycles: billingCycles,
    },
  }).then(res => res);

const apiRedeemReferralToken = ({ userId, code }) =>
  fetchApi(`api/v2/students/${userId}/referre_or_redeem`, 'POST', {
    data: {
      code,
    },
  }).then(res => res);

const apiUpdateUserAttributes = ({ userId, data }) =>
  fetchApi(`api/v2/users/${userId}/update_user_attributes`, 'PATCH', {
    data,
  }).then(res => res);

const apiFetchTutorStatisticalData = ({ userId }, request) =>
  fetchApi(`api/v2/tutors/${userId}/statistical_data`, 'GET', request);

const apiFetchPremiumPage = regionName =>
  getApi(`api/v4/regions/${regionName}/store_page`).then(res => res.data);

const apiFetchPlans = params =>
  getApi(`api/v4/addon/checkout_collections`, { params }).then(res => res.data);

const apiFetchChangeablePlans = ({ subscriptionId, action, token }) =>
  getApi(`api/v3/plans/changeable_plans`, {
    params: { subscription_id: subscriptionId, action, token },
  }).then(res => res.data);

const apiFetchConsentContent = planId =>
  getApi(`api/v3/plans/${planId}/consents`).then(res => res.data);

const apiFetchBTClientToken = () =>
  getApi('api/v2/payments/braintree/client_token').then(res => res);

const apiFetchBTPaymentMethodTokens = () =>
  fetchApi('api/v2/payments/braintree/payment_method_token', 'GET').then(
    res => res
  );

const apiValidateInvoice = ({ type, field }) =>
  postApi('api/v4/invoices/validation', {
    data: {
      invoice_category: type,
      [type]: field,
    },
  }).catch(error => {
    throw error.message;
  });

const apiCheckoutBuildPending = ({
  collectionId,
  paymentMethodId,
  paymentType,
  agentCode,
  code,
  bundleContents,
  appsflyerSale,
  deliveryInfo,
}) =>
  postApi('api/v3/payments/build_pending', {
    data: {
      agent_code: agentCode,
      checkout_collection_id: collectionId,
      code,
      payment_method_id: paymentMethodId,
      payment_type: paymentType,
      bundle_contents: bundleContents,
      appsflyer_sale: appsflyerSale,
      delivery_info: deliveryInfo,
    },
  }).then(res => res.data);

const apiCheckoutBraintreeNonce = ({
  orderNumber,
  collectionId,
  paymentMethodNonce,
  code,
  bundleContents,
  invoiceInfo,
}) =>
  fetchApi('api/v4/payments/braintree_gateway/checkout', 'POST', {
    data: {
      checkout_collection_id: collectionId,
      code,
      order_number: orderNumber,
      payment_method_nonce: paymentMethodNonce,
      bundle_contents: bundleContents,
      ...invoiceInfo,
    },
  }).then(res => res.data);

const apiCheckoutNeweb = ({
  orderNumber,
  collectionId,
  paymentType,
  code,
  bundleContents,
  invoiceInfo,
}) =>
  fetchApi('api/v4/payments/neweb_gateway/checkout', 'POST', {
    data: {
      checkout_collection_id: collectionId,
      code,
      order_number: orderNumber,
      payment_type: paymentType,
      bundle_contents: bundleContents,
      ...invoiceInfo,
    },
  }).then(res => res);

const apiCheckout2C2P = ({ paymentMethodId, code, agentCode }) => {
  return postApi('api/v3/payments/twoctwop/checkout', {
    params: {
      agent_code: agentCode,
      code,
      payment_method_id: paymentMethodId,
    },
  });
};

const apiRequestLinePay = ({
  orderNumber,
  collectionId,
  invoiceCategory,
  confirmUrl,
  cancelUrl,
  bundleContents,
  code,
}) => {
  return postApi('api/v4/payments/line_pay/request', {
    data: {
      order_number: orderNumber,
      checkout_collection_id: collectionId,
      invoice_category: invoiceCategory,
      confirm_url: confirmUrl,
      cancel_url: cancelUrl,
      bundle_contents: bundleContents,
      code,
    },
  });
};

const apiCheckoutLinePay = ({ orderNumber }) => {
  return postApi('api/v4/payments/line_pay/checkout', {
    data: {
      order_number: orderNumber,
    },
  }).then(res => res.data);
};

const apiCheckoutChangePlan = ({
  originalPlanId,
  paymentMethodId,
  action,
  paymentMethodNonce,
  code,
  invoiceInfo,
  appsflyerSale,
}) =>
  postApi('api/v3/braintree/change_plan', {
    data: {
      action,
      original_plan_id: originalPlanId,
      payment_method_id: paymentMethodId,
      payment_method_nonce: paymentMethodNonce,
      appsflyer_sale: appsflyerSale,
      code,
      ...invoiceInfo,
    },
  });

const apiForgotPasswordByEmail = ({ email, locale }) =>
  fetchApi('api/v2/users/send_reset_password_instructions', 'POST', {
    data: {
      email,
      domain: `${location.protocol}//${window.location.host}/${locale}/reset-password?token=`,
    },
  }).then(res => res);

const apiForgotPasswordByPhone = data =>
  postApi('api/v3/on_board/forget_password', {
    data,
  }).then(res => res);

const apiUpdatePhone = ({ userId, phone }) =>
  fetchApi(`api/v2/users/${userId}/update_phone`, 'PATCH', {
    data: {
      new_phone: phone,
      phone_verification: true,
    },
  }).then(res => res);

const apiResendPhoneConfirmationCode = ({ userId }) =>
  fetchApi(`api/v2/users/${userId}/resend_phone_verification`, 'POST').then(
    res => res
  );

const apiCheckPhoneVerificationCode = ({ userId, code }) =>
  fetchApi(`api/v2/users/${userId}/verify_phone`, 'POST', {
    data: {
      phone_code: code,
    },
  }).then(res => res);

const apiGetMarketingBanner = ({ region }) =>
  getApi('api/v2/web/banners', {
    params: {
      region_name: region,
    },
  }).then(res => res);

const apiGetIPRegionInfo = () => getApi('api/v3/ip_region').then(res => res);

const apiAppLaunch = data =>
  postApi('api/v2/app_launch', {
    data,
  }).then(res => res);

const apiFetchSiteInfo = () =>
  getApi('api/v3/web/guest/site_info').then(res => res.data);

const apiGetDeleteAccountReason = role =>
  fetchApi(`api/v3/delete_account_reasons?belong_role=${role}`);

const apiDeleteAccount = data =>
  deleteApi('api/v3/users', {
    data,
  });

const apiFetchTutorAuditInfo = () =>
  fetchApi('api/v3/tutors/audit_requirements').then(res => res.data);

const apiUpdateTutorAuditInfo = data =>
  fetchApi('api/v3/tutors/audit_requirements', 'PATCH', {
    data,
  }).then(res => res);

const apiSubmitTutorAudit = () =>
  fetchApi('api/v3/users/profile/submit', 'POST').then(res => res);

const apiFetchTutorAuditColleges = () =>
  fetchApi('api/v3/colleges').then(res => res.data);

const apiFetchTutorAuditCollegeMajors = collegeId =>
  fetchApi(`api/v3/colleges/${collegeId}/majors`).then(res => res.data);

const apiUpdateUserAvatar = ({ userId, isDefaultAvatar = false, data }) =>
  fetchApi(`api/v2/users/${userId}/update_profile_pic`, 'PATCH', {
    params: {
      is_default_avatar: isDefaultAvatar,
    },
    data,
  }).then(res => res);

const apiGetPaymentOrderDetailWithoutToken = orderNumber =>
  fetchApi(`api/v2/web/payments?order_number=${orderNumber}`).then(
    res => res.data
  );

const apiGetPaymentOrderDetailWithToken = orderNumber =>
  fetchApi(`api/v3/students/payments?order_number=${orderNumber}`).then(
    res => res.data
  );

const apiVerifyDiscountCode = ({ code, paymentMethodId }) =>
  postApi('api/v3/discounts/verify', {
    data: {
      code,
      payment_method_id: paymentMethodId,
    },
  });

const apiFetchRegionDeliveryInfo = region =>
  fetchApi(`api/v5/regions/${region}/delivery_info`).then(res => res);

const apiFetchUserLatestDeliveryInfo = () =>
  fetchApi(`api/v5/users/latest_delivery_info`).then(res => res);

const apiCheckDeliveryPhone = ({ phone }) =>
  postApi('api/v5/users/check_delivery_phone', {
    data: {
      phone,
    },
  });

const apiFetchStudentQuotas = userId =>
  getApi(`api/v2/students/${userId}/quotas`).then(res => res.data);

const apiGetAccountStatus = userId =>
  getApi(`api/v2/users/${userId}/account_status`).then(res => res.data);

const apiGet2C2PBanks = regionId =>
  getApi(`api/v3/regions/${regionId}/online_bankings`).then(res => res.data);

const apiSyncUserToZendesk = () => postApi('api/v3/users/zendesk_attrs');

const apiFetchWebMarquee = (page, region) =>
  getApi(`api/v3/web/web_marquee/${page}?region_name=${region}`).then(
    res => res.data
  );

const apiFetchRegionAuthType = region =>
  getApi(`api/v3/app_info?region_name=${region}`).then(res => res.data);

const apiPatchTutorProgressFinished = () =>
  fetchApi('api/v3/tutors/progress_finished', 'PATCH');

const apiFetchTutorList = ({ region, filterName, page = 1 }) =>
  getApi('api/v3/web/tutor_list', {
    params: {
      region_name: region,
      filter_name: filterName,
      page,
    },
  }).then(res => res.data);

const apiFetchTutorListFilterOptions = ({ region }) =>
  getApi('api/v3/web/tutor_filter_option', {
    params: {
      region_name: region,
    },
  }).then(res => res.data);

const apiUpdateBankInfo = data =>
  fetchApi('api/v3/tutors/bank', 'PATCH', { data }).then(res => res.data);

const apiFetchBankInfo = () =>
  getApi('api/v3/tutors/bank').then(res => res.data);
const apiFetchBankList = () =>
  getApi('api/v3/tutors/bank_lists').then(res => res.data);

// Zendesk
const apiSubmitTicketToZendesk = ({
  name,
  email,
  role,
  region,
  userId,
  message,
  studentId,
  tutorId,
  chatroomId,
  tokens,
  projectName,
  subjectTitle,
  tags,
  isReportToZendeskByAdmin,
}) => {
  return fetch(`${DRAGON_FRUIT_ENDPOINT}/submitTicketToZendesk`, {
    method: 'POST',
    data: {
      name,
      email,
      role,
      region,
      userId,
      message,
      studentId,
      tutorId,
      chatroomId,
      tokens,
      projectName,
      subjectTitle,
      tags,
      isReportToZendeskByAdmin,
    },
  }).catch(err => {
    throw new Error(err);
  });
};

const apiUploadFileToZendesk = file => {
  const data = new FormData();
  if (file) data.append('file', file);

  return fetch(`${DRAGON_FRUIT_ENDPOINT}/upload`, {
    method: 'POST',
    data,
  })
    .then(res => res.data.upload)
    .catch(err => {
      throw new Error(err);
    });
};

export {
  apiAppLaunch,
  apiCheckConfirmEmailToken,
  apiCheckout2C2P,
  apiCheckoutBraintreeNonce,
  apiCheckoutBuildPending,
  apiCheckoutChangePlan,
  apiCheckoutNeweb,
  apiRequestLinePay,
  apiCheckoutLinePay,
  apiCheckPhoneVerificationCode,
  apiCheckUpdatedEmailToken,
  apiDeleteAccount,
  apiFacebookSignup,
  apiFetchBankInfo,
  apiFetchBankList,
  apiFetchCurrentPlans,
  apiFetchBillingHistory,
  apiFetchBTClientToken,
  apiFetchBTPaymentMethodTokens,
  apiFetchCancelSubscriptionMethod,
  apiFetchChangeablePlans,
  apiFetchConsentContent,
  apiFetchCurriculums,
  apiFetchDistricts,
  apiFetchPlans as apiFetchAddon,
  apiFetchPremiumPage,
  apiFetchRegionAuthType,
  apiFetchRegions,
  apiFetchSchools,
  apiFetchSiteInfo,
  apiFetchStudentDistricts,
  apiFetchStudentQuotas,
  apiFetchStudentSchools,
  apiFetchTutorAuditCollegeMajors,
  apiFetchTutorAuditColleges,
  apiFetchTutorAuditInfo,
  apiFetchTutorList,
  apiFetchTutorListFilterOptions,
  apiFetchTutorStatisticalData,
  apiFetchUserInfo,
  apiFetchWebMarquee,
  apiForgotPasswordByEmail,
  apiForgotPasswordByPhone,
  apiGet2C2PBanks,
  apiGetAccountStatus,
  apiGetDeleteAccountReason,
  apiGetIPRegionInfo,
  apiGetMarketingBanner,
  apiGetOauthNowTVLoginUrl,
  apiGetPaymentOrderDetailWithoutToken,
  apiGetPaymentOrderDetailWithToken,
  apiLogin,
  apiLogout,
  apiOauthLogin,
  apiOauthNowTVLogin,
  apiPatchTutorProgressFinished,
  apiPauseSubscription,
  apiPostCancelSubscription,
  apiRedeemReferralToken,
  apiResendPhoneConfirmationCode,
  apiResetGuestPassword,
  apiResetUserEmail,
  apiResetUserPhone,
  apiResetUserPassword,
  apiSendEmailVerification,
  apiResendPhoneSignupVerification,
  apiEmailSignup,
  apiPhoneSignup,
  apiSubmitTicketToZendesk,
  apiSubmitTutorAudit,
  apiSyncUserToZendesk,
  apiUpdateBankInfo,
  apiUpdatePhone,
  apiUpdateTutorAuditInfo,
  apiUpdateUserAttributes,
  apiUpdateUserAvatar,
  apiUploadFileToZendesk,
  apiValidateInvoice,
  apiVerifyDiscountCode,
  apiVerifyEmail,
  apiCheckEmail,
  apiVerifyPhoneSignup,
  apiFetchRegionDeliveryInfo,
  apiFetchUserLatestDeliveryInfo,
  apiCheckDeliveryPhone,
};



// WEBPACK FOOTER //
// ./app/services/apis.js